import * as React from "react";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ParallelogramButton } from "@/components/ParallelogramButton";
import { BRAND, NAV_LINKS } from "@/constants/brand";

const Wordmark = () => (
  <a href="#top" className="group flex items-center gap-3 sm:gap-5">
    <img
      src={BRAND.logoMark}
      alt="Vision One Logo"
      className="h-10 w-auto animate-logo-glow transition-transform duration-500 ease-out group-hover:scale-[1.08] motion-safe:animate-logo-glow sm:h-14"
    />
    <img
      src={BRAND.logoWordmark}
      alt="Vision One – Webdesign & Marketing"
      className="hidden h-7 w-auto animate-logo-reveal opacity-90 transition-opacity duration-500 group-hover:opacity-100 sm:block sm:h-9"
    />
  </a>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 bg-[hsl(0_0%_0%)] transition-[border-color,box-shadow] duration-500 ${
        scrolled
          ? "border-b border-border/70 shadow-panel"
          : "border-b border-border/25"
      }`}
    >
      <nav className="mx-auto flex h-20 max-w-[1500px] items-center justify-between gap-6 px-5 sm:h-24 sm:px-8 lg:px-12">
        <Wordmark />

        <ul className="hidden items-center gap-9 lg:flex xl:gap-12">
          {NAV_LINKS.map((link) => (
            <li key={link.label}>
              <a
                href={link.href}
                className="group relative font-display text-[15px] font-light tracking-wide text-foreground/85 transition-colors duration-300 hover:text-foreground"
              >
                {link.label}
                <span className="absolute -bottom-2 left-0 h-px w-0 bg-gradient-hairline transition-[width] duration-300 ease-out group-hover:w-full" />
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden lg:block">
          <ParallelogramButton variant="subtle" size="sm" className="font-display font-light">
            Kostenlosen Entwurf anfragen
          </ParallelogramButton>
        </div>

        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Menü öffnen"
              className="lg:hidden text-foreground hover:bg-foreground/10 hover:text-foreground"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-[86vw] max-w-sm border-border/60 bg-background/95 backdrop-blur-2xl"
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">
              Hauptmenü von Vision One
            </SheetDescription>
            <div className="mt-10 flex flex-col gap-8">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="font-display text-2xl font-light tracking-wide text-foreground/90 transition-colors hover:text-primary"
                >
                  {link.label}
                </a>
              ))}
              <ParallelogramButton variant="primary" size="md" className="mt-4 w-full font-display font-light">
                Kostenlosen Entwurf anfragen
              </ParallelogramButton>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  );
};
